import React, { useState, useEffect, useRef, useContext, createContext } from 'react'

const PanelContext = createContext({
  panel: null,
  setHeight: () => {},
  direction: 'forward',
})

const Panel = ({ children, name, label }) => {
  const { panel, setHeight, direction } = useContext(PanelContext)
  const [ status, setStatus ] = useState(panel === name ? 'current' : null)
  const element = useRef()
  // Tracks the Panel.Collection's `panel` value as of the last
  // committed render. When status flips to 'current' AND this ref no
  // longer matches the new `panel`, the donor just transitioned in
  // (Continue / Back / picked a method) — pull focus into the panel
  // so the screen reader announces the region and Tab restarts here.
  // When they match — true on the very first paint of the initial
  // panel — we leave focus alone, matching embedded-widget convention
  // (Tab from the partner page reaches the widget header naturally).
  const prevPanelRef = useRef(panel)

  useEffect(() => {
    if (status !== 'current' || !element.current) return

    const handleResize = () =>
      element.current?.offsetHeight && setHeight(`${element.current.offsetHeight}px`)

    const observer = new ResizeObserver(handleResize)
    observer.observe(element.current)

    handleResize()
    const timeout = setTimeout(handleResize, 330)

    return () => {
      observer.disconnect()
      clearTimeout(timeout)
    }
  }, [ status ])

  useEffect(() => {
    if (status === 'current' && element.current && prevPanelRef.current !== panel)
      element.current.focus()
    prevPanelRef.current = panel
  }, [ status ])

  useEffect(() => {
    if (status !== 'transitioning') return
    const timeout = setTimeout(() => setStatus(null), 330)
    return () => clearTimeout(timeout)
  }, [ status ])

  useEffect(() => {
    setStatus(s => panel === name ? 'current' : s === 'current' ? 'transitioning' : null)
  }, [ panel ])

  return (
    <div
      aria-hidden={!status}
      className={[
        'widget-panel',
        status === 'current' && 'widget-panel-current',
        status === 'transitioning' && `widget-panel-leaving-${direction}`,
      ].filter(Boolean).join(' ')}
    >
      {/* Inner container is the focus target on transition. role +
          aria-label make it a labelled landmark so the announcement
          on focus carries meaning ("Almost there, region"). tabindex=-1
          makes it programmatically focusable; outline-none suppresses
          the focus ring since this is container-level focus that the
          donor didn't initiate by Tab. */}
      <div
        ref={element}
        tabIndex={-1}
        role={label ? 'region' : undefined}
        aria-label={label}
        className="widget-panel-inner focus:outline-none"
      >
        {status && children}
      </div>
    </div>
  )
}

Panel.Collection = ({ children, panel }) => {
  const [ height, setHeight ] = useState('16rem')
  const panelNames = React.Children.map(children, child => child?.props?.name).filter(Boolean)
  const prevPanelRef = useRef(panel)
  const directionRef = useRef('forward')

  // Lock direction in at the moment panel changes and keep it stable across
  // subsequent re-renders. If we recomputed every render, the second render
  // (triggered by Panel's setStatus) would see prev === current and flip
  // direction back to 'forward', giving the transitioning panel the wrong
  // leaving class.
  if (prevPanelRef.current !== panel) {
    const prevIndex = panelNames.indexOf(prevPanelRef.current)
    const currentIndex = panelNames.indexOf(panel)
    directionRef.current = currentIndex >= prevIndex ? 'forward' : 'backward'
    prevPanelRef.current = panel
  }

  return (
    <div className="widget-content" style={{ height }}>
      <PanelContext.Provider value={{ panel, setHeight, direction: directionRef.current }}>
        {children}
      </PanelContext.Provider>
    </div>
  )
}

export default Panel
